import React, { createContext, useContext, useEffect, useMemo, useRef } from 'react';
import __noop from '@atlaskit/ds-lib/noop';
import { getBooleanFF } from '@atlaskit/platform-feature-flags';

/**
 *
 *  @internal
 * LevelContext which maintains the current level of nested layers
 * Default is 0
 */
export const LevelContext = /*#__PURE__*/createContext(0);

/**
 *
 *  @internal
 * TopLevelContext which maintains the top level ref and setTopLevel method of layers
 * Default ref value is null
 *
 */
export const TopLevelContext = /*#__PURE__*/createContext({
  topLevelRef: {
    current: null
  },
  setTopLevel: __noop
});

/**
 *
 *  @internal
 * Context Provider Component which provider the current level of nested layers
 * It handles level management when it mounts and unmounts
 *
 */
const LevelProvider = ({
  children,
  currentLevel
}) => {
  const {
    setTopLevel
  } = useContext(TopLevelContext);
  setTopLevel(currentLevel);
  useEffect(() => {
    return () => setTopLevel(currentLevel - 1);
  }, [setTopLevel, currentLevel]);
  return /*#__PURE__*/React.createElement(LevelContext.Provider, {
    value: currentLevel
  }, children);
};

/**
 *
 *  @internal
 * Context Provider Component which provides the top level of all nested layers
 * It provides initial top level ref value as 0 and set top level method
 *
 */
const LayeringProvider = ({
  children
}) => {
  const topLevelRef = useRef(0);
  const value = useMemo(() => ({
    topLevelRef,
    setTopLevel: level => {
      topLevelRef.current = level;
    }
  }), [topLevelRef]);
  return /*#__PURE__*/React.createElement(TopLevelContext.Provider, {
    value: value
  }, children);
};

/**
 * __UNSAFE_LAYERING__
 *
 * @experimental Still under development. Do not use.
 *
 * @important the component is under feature flag platform.design-system-team.layering_qmiw3
 *
 * Layering component is a wrapper to let children to consume layer contexts and hooks.
 *
 */
export const UNSAFE_LAYERING = ({
  children
}) => {
  const currentLevel = useContext(LevelContext);
  const isNested = currentLevel > 0;
  if (!getBooleanFF('platform.design-system-team.layering_qmiw3')) {
    return /*#__PURE__*/React.createElement(React.Fragment, null, children);
  }
  const content = /*#__PURE__*/React.createElement(LevelProvider, {
    currentLevel: currentLevel + 1
  }, children);
  return isNested ? content : /*#__PURE__*/React.createElement(LayeringProvider, null, content);
};